import { NAV_SECTIONS } from '@/shared/lib/navigation';
import type { PassbookFlagKey, PassbookPermissionRow } from '../api/use-permissions';

/** Recurso do programa que precisa estar ligado para a página fazer sentido. */
type ProgramModuleKey = 'hasTiers';

export interface PageFlag {
  key: PassbookFlagKey;
  label: string;
  /** O que muda no painel da loja. Só no tooltip: curto e no imperativo. */
  effect: string;
  /** Some de Páginas enquanto o recurso estiver desligado em Recursos. */
  requires?: ProgramModuleKey;
  /** Liga o módulo no passbook: o switch fica em Recursos, não em Páginas. */
  module?: boolean;
  /** Só em Recursos. Não entra em Páginas nem no usuário. */
  programOnly?: boolean;
  /** Só no usuário. Não há switch no passbook; sem override, segue `defaultOn`. */
  userOnly?: boolean;
  /** Padrão do perfil Base quando o usuário não escolheu libera nem bloqueia. */
  defaultOn?: boolean;
}

export interface PageFlagGroup {
  section: string;
  flags: PageFlag[];
}

/**
 * Flags que já decidem o menu (navigation.ts), agrupadas pelas seções da
 * sidebar para o master achar pelo mesmo nome que a loja vê.
 */
export const PAGE_FLAG_GROUPS: PageFlagGroup[] = [
  {
    section: NAV_SECTIONS.results,
    flags: [
      {
        key: 'hasDashboard',
        label: 'Painel',
        effect: 'Libera o Painel. No perfil Base, liberado por padrão.',
        userOnly: true,
        defaultOn: true,
      },
    ],
  },
  {
    section: NAV_SECTIONS.customers,
    flags: [
      {
        key: 'hasLeads',
        label: 'Leads',
        effect: 'Libera Leads e o detalhe de cada lead. No perfil Base, liberado por padrão.',
        userOnly: true,
        defaultOn: true,
      },
      {
        key: 'hasSegmentations',
        label: 'Audiência',
        effect: 'Libera Audiência e o assistente de IA que monta públicos.',
        module: true,
      },
      {
        key: 'notShowUsers',
        label: 'Esconder leads de perfis restritos',
        effect: 'Leads some para perfis além de Client, Operator, Admin e Owner.',
      },
    ],
  },
  {
    section: NAV_SECTIONS.engagement,
    flags: [
      {
        key: 'hasCampaignAdvanced',
        label: 'Push',
        effect: 'Libera o canal Push: lista e envio.',
        module: true,
      },
      {
        key: 'hasEmailMarketing',
        label: 'E-mail',
        effect: 'Libera o canal E-mail: campanhas, templates e envio. Não aparece em Páginas.',
        module: true,
        programOnly: true,
      },
      {
        key: 'hasSmsMarketing',
        label: 'SMS',
        effect: 'Libera o canal SMS: campanhas, texto e revisão. Não liga o e-mail.',
        module: true,
        programOnly: true,
      },
      {
        key: 'hasCampaign',
        label: 'Mensagem no cartão',
        effect: 'Mensagem manual no cartão. Some quando a loja tem o canal Push.',
        module: true,
      },
      {
        key: 'hasCampaignMetabase',
        label: 'Upload de lista',
        effect: 'Libera Upload de lista (arquivos do BI).',
        module: true,
      },
      {
        key: 'hasSimpleCampaignWhatsApp',
        label: 'WhatsApp',
        effect: 'Flag do legado. Não abre tela neste painel.',
        module: true,
      },
    ],
  },
  {
    section: NAV_SECTIONS.loyalty,
    flags: [
      {
        key: 'hasLoyaltyCampaigns',
        label: 'Campanhas de fidelidade',
        effect: 'Libera o motor de campanhas de fidelidade e a liquidação.',
        module: true,
      },
      {
        key: 'hasMemberGetMember',
        label: 'Indique e ganhe',
        effect: 'Libera relatório, aba no lead e botão flutuante. A campanha só o master configura.',
        module: true,
      },
      {
        key: 'hasProducts',
        label: 'Produtos',
        effect: 'Libera Produtos e Resgates.',
        module: true,
      },
      {
        key: 'hasInfluencerCard',
        label: 'Parceiros',
        effect: 'Libera o cadastro de influencers (cartão com arte alternativa).',
        module: true,
      },
      {
        key: 'hasTierSettings',
        label: 'Tiers',
        effect: 'Libera a página Tiers. O master vê sempre.',
        requires: 'hasTiers',
      },
      {
        key: 'hasTierOverride',
        label: 'Ajustar cliente',
        effect: 'Libera Ajustar cliente. O master vê sempre.',
        requires: 'hasTiers',
      },
    ],
  },
  {
    section: NAV_SECTIONS.card,
    flags: [
      {
        key: 'hasBanners',
        label: 'Banners',
        effect: 'Libera Banners. Sem escolha própria, bloqueada.',
        userOnly: true,
      },
      {
        key: 'hasFabButtons',
        label: 'Botões flutuantes',
        effect: 'Libera Botões flutuantes. Sem escolha própria, bloqueada.',
        userOnly: true,
      },
    ],
  },
  {
    section: NAV_SECTIONS.pointOfSale,
    flags: [
      {
        key: 'hasMultiStore',
        label: 'Multi-loja',
        effect: 'Filtros por loja em Leads e campanhas; Mensagem no cartão para operadores.',
        module: true,
      },
      {
        key: 'hasOperator',
        label: 'Operador',
        effect: 'Libera a tela de Operador. No perfil Base, liberado por padrão.',
        userOnly: true,
        defaultOn: true,
      },
    ],
  },
  {
    section: NAV_SECTIONS.store,
    flags: [
      { key: 'hasConfig', label: 'Configurações', effect: 'Libera Configurações para o Client.' },
      {
        key: 'hasAudit',
        label: 'Auditoria',
        effect: 'Libera Auditoria para Admin, Client e Owner. O master vê sempre.',
      },
    ],
  },
];

type FlagRow = Pick<PassbookPermissionRow, 'hasTiers' | 'flags'>;

function filterGroups(keep: (flag: PageFlag) => boolean): PageFlagGroup[] {
  return PAGE_FLAG_GROUPS.map((group) => ({ ...group, flags: group.flags.filter(keep) })).filter(
    (group) => group.flags.length > 0,
  );
}

/** Recursos que ligam um módulo no passbook — moram em Recursos, não em Páginas. */
export const PROGRAM_MODULE_FLAGS: PageFlag[] = PAGE_FLAG_GROUPS.flatMap((group) =>
  group.flags.filter((flag) => flag.module),
);

/** Páginas do passbook: sem módulos, sem páginas só do usuário e sem recurso desligado. */
export function passbookPageFlagGroups(passbook: FlagRow): PageFlagGroup[] {
  return filterGroups((flag) => !flag.module && !flag.userOnly && (!flag.requires || passbook[flag.requires]));
}

/**
 * Card do usuário: todas as páginas da loja, mesmo com o módulo desligado no
 * passbook. O master libera ou bloqueia uma a uma.
 */
export function userPageFlagGroups(): PageFlagGroup[] {
  return filterGroups((flag) => !flag.programOnly);
}
